import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categories = [
  'Education', 'Healthcare', 'Food Distribution', 'Women Empowerment',
  'Senior Citizens', 'Disaster Relief', 'Animal Welfare', 'Environmental Projects',
  'Child Welfare', 'Community Development', 'Skill Development', 'Emergency Support',
];

export default function StartFundraiserPage() {
  const [formData, setFormData] = useState({
    campaignTitle: '',
    category: '',
    targetAmount: '',
    description: '',
    fullName: '',
    email: '',
    phone: '',
    city: '',
    panNumber: '',
    bankAccount: '',
    ifsc: '',
    company_alt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('idle');
    setFormError('');

    if (!formData.campaignTitle || !formData.category || !formData.targetAmount || !formData.description) {
      setFormError('Please fill in all required campaign fields.');
      setSubmitStatus('error');
      return;
    }
    if (!formData.fullName || !formData.email || !formData.phone) {
      setFormError('Please fill in all required personal information fields.');
      setSubmitStatus('error');
      return;
    }

    const honeypot = (e.currentTarget.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitStatus('success');
      setFormData({ campaignTitle: '', category: '', targetAmount: '', description: '', fullName: '', email: '', phone: '', city: '', panNumber: '', bankAccount: '', ifsc: '', company_alt: '' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData(e.currentTarget);
      const response = await fetch('https://readdy.ai/api/form/d9eud4frf0c7mtq82t70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormData({ campaignTitle: '', category: '', targetAmount: '', description: '', fullName: '', email: '', phone: '', city: '', panNumber: '', bankAccount: '', ifsc: '', company_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setSubmitStatus('success');
          setFormData({ campaignTitle: '', category: '', targetAmount: '', description: '', fullName: '', email: '', phone: '', city: '', panNumber: '', bankAccount: '', ifsc: '', company_alt: '' });
        } else {
          setFormError(serverMsg || 'Something went wrong. Please try again.');
          setSubmitStatus('error');
        }
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 bg-gradient-to-br from-accent-500 via-accent-400 to-primary-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-flag-line mr-1.5"></i>Start a Campaign
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4">Start a Fundraiser</h1>
            <p className="text-white/80 text-sm md:text-lg max-w-2xl mx-auto">
              Turn your passion into action. Create a fundraising campaign for a cause close to your heart and rally your community to make a difference.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-18 bg-background-50">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
              {submitStatus === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-3xl"></i>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Fundraiser Submitted!</h2>
                  <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
                    Your fundraiser application has been received. Our team will verify the details and get back to you within 3-5 business days. Once approved, your campaign will go live on our platform.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="px-6 py-2.5 bg-accent-500 text-white text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors"
                  >
                    Create Another Fundraiser
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Create Your Fundraiser</h2>
                  <p className="text-foreground-500 text-sm mb-6">Tell us about your campaign and we'll help you bring it to life.</p>

                  {submitStatus === 'error' && formError && (
                    <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
                  )}

                  <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-6">
                    {/* Honeypot */}
                    <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }}>
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    {/* Campaign Details */}
                    <div className="pb-6 border-b border-background-200">
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-flag-line text-accent-500"></i> Campaign Details
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Campaign Title *</label>
                          <input
                            type="text"
                            name="campaignTitle"
                            value={formData.campaignTitle}
                            onChange={(e) => updateField('campaignTitle', e.target.value)}
                            required
                            placeholder="e.g., Build a Library for Village Children"
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Category *</label>
                            <select
                              name="category"
                              value={formData.category}
                              onChange={(e) => updateField('category', e.target.value)}
                              required
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 appearance-none cursor-pointer"
                              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                            >
                              <option value="">Select category</option>
                              {categories.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Target Amount (₹) *</label>
                            <input
                              type="number"
                              name="targetAmount"
                              value={formData.targetAmount}
                              onChange={(e) => updateField('targetAmount', e.target.value)}
                              required
                              placeholder="e.g., 100000"
                              min="5000"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Campaign Description *</label>
                          <textarea
                            name="description"
                            value={formData.description}
                            onChange={(e) => updateField('description', e.target.value)}
                            required
                            placeholder="Describe your campaign — what's the cause, who will benefit, and how will the funds be used?"
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400 resize-none"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">{500 - formData.description.length} characters remaining</p>
                        </div>
                      </div>
                    </div>

                    {/* Personal Information */}
                    <div className="pb-6 border-b border-background-200">
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-user-line text-accent-500"></i> Personal Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Full Name *</label>
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={(e) => updateField('fullName', e.target.value)}
                              required
                              placeholder="Enter your full name"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Email Address *</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={(e) => updateField('email', e.target.value)}
                              required
                              placeholder="your@email.com"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Phone Number *</label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={(e) => updateField('phone', e.target.value)}
                              required
                              placeholder="+91 98765 43210"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">City</label>
                            <input
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={(e) => updateField('city', e.target.value)}
                              placeholder="Your city"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Verification & Bank Details */}
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-shield-check-line text-accent-500"></i> Verification & Bank Details
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">PAN Number</label>
                          <input
                            type="text"
                            name="panNumber"
                            value={formData.panNumber}
                            onChange={(e) => updateField('panNumber', e.target.value.toUpperCase())}
                            placeholder="ABCDE1234F"
                            maxLength={10}
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 uppercase placeholder:text-foreground-400"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Bank Account Number</label>
                            <input
                              type="text"
                              name="bankAccount"
                              value={formData.bankAccount}
                              onChange={(e) => updateField('bankAccount', e.target.value)}
                              placeholder="Enter account number"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">IFSC Code</label>
                            <input
                              type="text"
                              name="ifsc"
                              value={formData.ifsc}
                              onChange={(e) => updateField('ifsc', e.target.value.toUpperCase())}
                              placeholder="e.g., SBIN0001234"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-foreground-900 uppercase placeholder:text-foreground-400"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200">
                      <i className="ri-information-line text-foreground-400 text-lg mt-0.5 flex-shrink-0"></i>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        By submitting this form, you agree to Happy Faces Society's fundraiser guidelines. All campaigns are reviewed for authenticity before going live. You'll need to submit identity verification documents and campaign images after initial approval.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-accent-500 text-white text-base font-bold rounded-xl hover:bg-accent-600 disabled:bg-foreground-300 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i> Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ri-flag-line"></i> Submit Fundraiser
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}